import { useEffect, useId, useState } from 'react';
import { Button } from '../components/Button/Button';
import { AddChildDialog } from './AddChildDialog';
import { useChildren, useDeleteChild } from './useAuth';
import './ChildrenDialog.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Parent's view of their kids.
 *
 * Shows:
 *  - A list of child profiles with display name + birth year +
 *    suspension chip if applicable.
 *  - A "Remove" affordance per child that double-confirms.
 *  - An "+ Add a kid" button that opens the AddChildDialog.
 *
 * Sign-in as a kid happens via the dedicated child-signin tab on
 * the regular AuthDialog, not here. This pane is the parent's
 * management surface — it never holds a kid's PIN.
 */
export function ChildrenDialog({ isOpen, onClose }: Props): JSX.Element | null {
  const titleId = useId();
  // Only fetch children when the dialog is actually open — the
  // hook's `enabled` gate prevents the chip-mounted-but-closed
  // dialog from pre-fetching on every parent's render.
  const children = useChildren({ enabled: isOpen });
  const deleteChild = useDeleteChild();
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setAddOpen(false);
      setPendingDelete(null);
      deleteChild.reset();
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !addOpen && !deleteChild.isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, addOpen]);

  if (!isOpen) return null;

  const onRemoveClick = (id: string): void => {
    if (pendingDelete !== id) {
      setPendingDelete(id);
      return;
    }
    deleteChild.mutate({ childId: id }, { onSuccess: () => setPendingDelete(null) });
  };

  return (
    <>
      <div
        className="bl-children__backdrop"
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget && !deleteChild.isPending && !addOpen) {
            onClose();
          }
        }}
      >
        <div
          className="bl-children__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <h2 id={titleId} className="bl-children__title">
            Your kids
          </h2>
          <p className="bl-children__lead">
            Add a profile for each kid. They sign in with your email, their display name, and the
            PIN you set.
          </p>

          {children.isPending && <p className="bl-children__status">Loading…</p>}
          {children.error && (
            <p className="bl-children__error" role="alert">
              {children.error.message}
            </p>
          )}

          {children.data && children.data.length === 0 && (
            <p className="bl-children__empty">You haven&rsquo;t added any kids yet.</p>
          )}

          {children.data && children.data.length > 0 && (
            <ul className="bl-children__list" aria-label="Your kids">
              {children.data.map((c) => (
                <li key={c.id} className="bl-children__row">
                  <div className="bl-children__info">
                    <strong>{c.display_name}</strong>
                    <span>
                      Born {c.birth_year ?? '—'}
                      {c.suspended ? ' · paused' : ''}
                    </span>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => onRemoveClick(c.id)}
                    disabled={deleteChild.isPending}
                  >
                    {pendingDelete === c.id ? 'Click again to remove' : 'Remove'}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {deleteChild.error && (
            <p className="bl-children__error" role="alert">
              {deleteChild.error.message}
            </p>
          )}

          <div className="bl-children__actions">
            <Button onClick={() => setAddOpen(true)}>+ Add a kid</Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      <AddChildDialog isOpen={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
}
